import axios, { AxiosRequestConfig, Method } from 'axios';
import { environmentConfig } from '../config';
import { Environment, ENVIRONMENT_HEADER } from '@mapp-byte-connect/config';

type TRequestParams = Record<string, unknown> | unknown;
type TRequestHeaders = Record<string, string>;
const BRAND_HEADER = 'x-brand-id';

class NetworkManager {
  private static instance: NetworkManager;
  private authToken: string | null = null;
  private environment: Environment = 'local';
  private brandId: string | null = null;

  static getInstance(): NetworkManager {
    if (!NetworkManager.instance) {
      NetworkManager.instance = new NetworkManager();
    }

    return NetworkManager.instance;
  }

  setAuthorizationToken(token: string) {
    this.authToken = token;
  }

  clearAuthorizationToken() {
    this.authToken = null;
  }

  setEnvironment(environment: Environment) {
    this.environment = environment;
  }

  setBrandId(brandId: string | null) {
    this.brandId = brandId;
  }

  getEnvironment(): Environment {
    return this.environment;
  }

  async requestHandler(
    method: Method,
    path: string,
    params?: TRequestParams,
    customHeaders?: TRequestHeaders
  ) {
    const headers: TRequestHeaders = {
      ...(this.authToken ? { Authorization: `Bearer ${this.authToken}` } : {}),
      [ENVIRONMENT_HEADER]: this.environment,
      ...(this.brandId ? { [BRAND_HEADER]: this.brandId } : {}),
      ...({
        'Content-Type': 'application/json',
      } as TRequestHeaders),
      ...(customHeaders || {}),
    };

    const config: AxiosRequestConfig = {
      method: method,
      url: `${environmentConfig.API_BASE_URL}${path}`,
      headers,
    };

    const normalizedMethod = String(method).toUpperCase();
    if (normalizedMethod === 'GET' || normalizedMethod === 'DELETE') {
      config.params = params;
    } else {
      config.data = params;
    }

    try {
      const response = await axios(config);
      return response.data;
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        console.error('API error:', error.response?.data || error.message);
      } else {
        console.error('API error:', error);
      }
      throw error;
    }
  }

  async get<T>(
    path: string,
    params?: Record<string, unknown>,
    headers?: TRequestHeaders
  ): Promise<T> {
    return this.requestHandler('get', path, params, headers);
  }

  async post<T>(
    path: string,
    params?: Record<string, unknown> | unknown,
    headers?: TRequestHeaders
  ): Promise<T> {
    return this.requestHandler('post', path, params, headers);
  }

  async put<T>(
    path: string,
    params?: Record<string, unknown>,
    headers?: TRequestHeaders
  ): Promise<T> {
    return this.requestHandler('put', path, params, headers);
  }

  async delete<T>(
    path: string,
    params?: Record<string, unknown> | unknown,
    headers?: TRequestHeaders
  ): Promise<T> {
    return this.requestHandler('delete', path, params, headers);
  }
}

export default NetworkManager.getInstance();
